/**
 * Sparkline de TENDÊNCIA do resultado (#251 / #253 casar o alvo / #50 hover) —
 * VARIAÇÃO ACUMULADA do `fv_diario` (running sum; null=0). Enquadramento
 * CONTIDO (altura 72px, padding lateral) — a linha não achata e os dots são
 * CÍRCULOS de raio fixo (divs HTML overlay, nunca elipse esticada). Markers
 * com label SOB o ponto real; #50: labels em ATÉ 2 LINHAS (anti-colisão — na
 * janela ~30d a âncora "1 mês" pode cair colada no "hoje" logo após a virada).
 *
 * #50 TOOLTIP: hover em QUALQUER ponto → {data do DADO, acumulado, Δ vs ponto
 * anterior}. A data é o `as_of` do ponto (data do PREGÃO que o número
 * representa — fix dcd34db), NUNCA `data` (data de gravação): as_of ausente →
 * "as-of ?" honesto, sem fallback pro relógio. Ponto com fv_diario null → o
 * Δ diz "sem dado no dia" (nada inventado; o acumulado só carrega o nível).
 *
 * FAIL-CLOSED (#50): `error` → estado de ERRO explícito (a tela DIZ), nunca o
 * empty state (que significaria "track-record ainda não começou") nem linha
 * de fallback — este componente não tem mock próprio (mockImportGuard cobre o
 * import; aqui não existe caminho de dado que não venha da série da API).
 */
import { useState } from "react";
import { fmtBRLcompactSigned } from "../../utils/formatters";
import type { AnotacaoCorrecao, CurvaPnlSerieResponse, CurvaPnlSeriePonto } from "../../api/types";

interface Props {
  serie: CurvaPnlSeriePonto[];
  ancoras: CurvaPnlSerieResponse["ancoras"];
  error?: Error | null;
  /** 28/09: pontos com anotação de correção ganham marcador âmbar + linha no
   * tooltip. Gatilho = anotação presente na série (estado), nunca a data. */
  anotacoes?: AnotacaoCorrecao[];
}

const COR_ANOT = "#fbbf24"; // amber-400 — mesmo tom dos avisos do card

const VB_W = 1000;
const VB_H = 72;
const PADX = 2; // % lateral (dot/label não encostam na borda)
const PADY = 8; // viewBox units (amplitude vertical contida)
const COR = "#34d399"; // emerald — casa o --pos do alvo
const COR_SEC = "#7fded6";
// #50: distância mínima (em % do eixo x) entre CENTROS de labels na mesma
// linha; abaixo disso o label desce pra 2ª linha (senão "1 mês"/"1 sem"
// sobrepõem o "hoje" quando as âncoras caem coladas no fim da janela).
const LABEL_GAP_PCT = 12;

/** ISO "YYYY-MM-DD" → "DD/MM" (string pura — imune a timezone). */
function ddmm(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

function Caption() {
  return (
    <div className="mb-1">
      <span className="text-[11px] text-slate-400" data-testid="resultado-sparkline-caption">
        variação acumulada do resultado · ~30d
      </span>
    </div>
  );
}

export function ResultadoSparkline({ serie, ancoras, error = null, anotacoes = [] }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const n = serie.length;
  const temDado = serie.some((p) => p.fv_diario !== null);

  // FAIL-CLOSED (#50): erro de API é ERRO — não é "série ainda não começou".
  if (error) {
    return (
      <div>
        <Caption />
        <div
          className="h-[72px] flex items-center text-[11px] text-amber-300/80"
          data-testid="resultado-sparkline-erro"
        >
          série do resultado indisponível (erro ao carregar) — sem gráfico
        </div>
      </div>
    );
  }

  if (n === 0 || !temDado) {
    return (
      <div>
        <Caption />
        <div
          className="h-[72px] flex items-center text-[11px] text-slate-500"
          data-testid="resultado-sparkline-vazio"
        >
          sem série diária ainda — acende com o track-record (#68)
        </div>
      </div>
    );
  }

  // Acumulado: soma corrente do fv_diario (null = 0), cronológico.
  const cum: number[] = [];
  let acc = 0;
  for (const p of serie) {
    acc += p.fv_diario ?? 0;
    cum.push(acc);
  }

  const xPct = (i: number) => (n === 1 ? 50 : PADX + (i / (n - 1)) * (100 - 2 * PADX));
  let lo = Math.min(0, ...cum);
  let hi = Math.max(0, ...cum);
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  }
  const yV = (v: number) => PADY + (1 - (v - lo) / (hi - lo)) * (VB_H - 2 * PADY);
  const yPct = (v: number) => (yV(v) / VB_H) * 100;

  // preserveAspectRatio="none" só na LINHA (estica linearmente em x e y → os dots
  // HTML overlay alinham ao pixel); os dots são divs round, imunes ao stretch.
  const poly = cum.map((v, i) => `${((xPct(i) / 100) * VB_W).toFixed(1)},${yV(v).toFixed(1)}`).join(" ");
  const zeroV = yV(0).toFixed(1);
  const idxOf = (iso: string | null) => (iso ? serie.findIndex((p) => p.data === iso) : -1);

  const markers = [
    { key: "mes", label: "1 mês", i: idxOf(ancoras.dmes_date) },
    { key: "semana", label: "1 sem", i: idxOf(ancoras.dsem_date) },
    { key: "hoje", label: "hoje", i: n - 1 },
  ].filter((m) => m.i >= 0);

  // #50 anti-colisão: greedy por x crescente — label que ficaria a menos de
  // LABEL_GAP_PCT do último label mantido NA MESMA linha desce pra linha 2.
  const ordenados = [...markers].sort((a, b) => xPct(a.i) - xPct(b.i));
  const ultimoX: number[] = [-Infinity, -Infinity];
  const rowDe = new Map<string, number>();
  for (const m of ordenados) {
    const x = xPct(m.i);
    const row = x - ultimoX[0] >= LABEL_GAP_PCT ? 0 : 1;
    ultimoX[row] = x;
    rowDe.set(m.key, row);
  }
  const temSegundaLinha = [...rowDe.values()].some((r) => r === 1);

  // 28/09: pontos ANOTADOS (valor gravado mantido; o marcador diz que o ponto
  // tem correção conhecida — o acumulado NÃO é refeito com o correto).
  const anotPorIdx = new Map<number, AnotacaoCorrecao>();
  for (const a of anotacoes) {
    const i = serie.findIndex((p) => p.data === a.data_referencia);
    if (i >= 0) anotPorIdx.set(i, a);
  }

  // #50 tooltip: largura do "slot" de hover de cada ponto (em %).
  const slotW = n === 1 ? 100 : (100 - 2 * PADX) / (n - 1);
  const hoverP = hover !== null ? serie[hover] : null;

  return (
    <div data-testid="resultado-sparkline">
      <Caption />
      <div className="relative" style={{ height: `${VB_H}px` }}>
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="none"
          className="w-full h-full"
          role="img"
          aria-label="Variação acumulada do resultado (~30 dias úteis)"
        >
          <line x1={0} x2={VB_W} y1={zeroV} y2={zeroV} stroke="rgba(255,255,255,0.10)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          {n > 1 && (
            <polyline points={poly} fill="none" stroke={COR} strokeWidth={2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          )}
        </svg>
        {/* dots HTML = CÍRCULOS de raio fixo (px), nunca elipse */}
        {markers.map((m) => {
          const d = m.key === "hoje" ? 8 : 6;
          return (
            <div
              key={m.key}
              data-testid={`sparkline-marker-${m.key}`}
              style={{
                position: "absolute",
                left: `${xPct(m.i)}%`,
                top: `${yPct(cum[m.i])}%`,
                width: `${d}px`,
                height: `${d}px`,
                borderRadius: "50%",
                transform: "translate(-50%, -50%)",
                background: m.key === "hoje" ? COR : COR_SEC,
                border: m.key === "hoje" ? "1.5px solid #001528" : "none",
              }}
            />
          );
        })}
        {/* 28/09: marcador de ANOTAÇÃO — anel âmbar no ponto gravado */}
        {[...anotPorIdx.keys()].map((i) => (
          <div
            key={`anot-${i}`}
            data-testid={`sparkline-marker-anotacao-${serie[i].data}`}
            title="valor gravado com correção conhecida (ver nota no cartão)"
            style={{
              position: "absolute",
              left: `${xPct(i)}%`,
              top: `${yPct(cum[i])}%`,
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              transform: "translate(-50%, -50%)",
              border: `2px solid ${COR_ANOT}`,
              background: "transparent",
              pointerEvents: "none",
            }}
          />
        ))}
        {/* #50: dot de hover (círculo fixo, mesmo padrão dos markers) */}
        {hover !== null && (
          <div
            data-testid="sparkline-hover-dot"
            style={{
              position: "absolute",
              left: `${xPct(hover)}%`,
              top: `${yPct(cum[hover])}%`,
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              transform: "translate(-50%, -50%)",
              background: "#fff",
              pointerEvents: "none",
            }}
          />
        )}
        {/* #50: áreas de hover por ponto (invisíveis, 1 slot por ponto) */}
        {serie.map((_, i) => (
          <div
            key={i}
            data-testid={`sparkline-hit-${i}`}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            style={{
              position: "absolute",
              left: `${xPct(i)}%`,
              top: 0,
              width: `${slotW}%`,
              height: "100%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          />
        ))}
        {/* #50: tooltip — data do DADO (as_of), acumulado e Δ vs anterior */}
        {hover !== null && hoverP !== null && (
          <div
            data-testid="sparkline-tooltip"
            className="absolute z-20 px-2 py-1 rounded border border-white/10 text-[10px] leading-tight whitespace-nowrap pointer-events-none"
            style={{
              left: `${Math.min(82, Math.max(18, xPct(hover)))}%`,
              top: "-6px",
              transform: "translate(-50%, -100%)",
              background: "#001528",
            }}
          >
            <div className="text-white/80 font-medium" data-testid="sparkline-tooltip-data">
              {/* data do PREGÃO (as_of) — nunca a data de gravação */}
              {hoverP.as_of ? ddmm(hoverP.as_of) : "as-of ?"}
            </div>
            <div className="text-emerald-300 font-mono tabular-nums" data-testid="sparkline-tooltip-acum">
              acum {fmtBRLcompactSigned(cum[hover])}
            </div>
            <div className="text-white/60 font-mono tabular-nums" data-testid="sparkline-tooltip-delta">
              {hover === 0
                ? "Δ — (início da janela)"
                : hoverP.fv_diario === null
                  ? "Δ sem dado no dia"
                  : `Δ ${fmtBRLcompactSigned(cum[hover] - cum[hover - 1])}`}
            </div>
            {anotPorIdx.has(hover) && anotPorIdx.get(hover)!.valores.diario && (
              <div className="text-amber-300" data-testid="sparkline-tooltip-anotacao">
                gravado com correção · Δ correto{" "}
                {fmtBRLcompactSigned(anotPorIdx.get(hover)!.valores.diario.correto_vf)}
              </div>
            )}
          </div>
        )}
      </div>
      {/* labels SOB o ponto real (mesmo x% do dot); #50: até 2 linhas */}
      <div className={`relative mt-0.5 ${temSegundaLinha ? "h-7" : "h-3.5"}`}>
        {markers.map((m) => {
          const row = rowDe.get(m.key) ?? 0;
          return (
            <span
              key={m.key}
              data-testid={`sparkline-label-${m.key}`}
              data-row={row}
              className={`absolute text-[9px] uppercase tracking-wider ${
                m.key === "hoje" ? "text-emerald-300" : "text-slate-500"
              }`}
              style={{
                left: `${xPct(m.i)}%`,
                top: `${row * 13}px`,
                transform: "translateX(-50%)",
              }}
            >
              {m.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}
